// Import core-js for polyfills, mostly for IE11
import "core-js/stable";
import "regenerator-runtime/runtime";
import "intersection-observer";
import "./polyfills/append.polyfill";
import "./polyfills/closest.polyfill";
import Vue from "vue";
import Vuex from "vuex";
import UniqueId from "vue-unique-id";
import router from "./router";
import { ImagePlugin, ModalPlugin } from "bootstrap-vue";
import InstantSearch from "vue-instantsearch";
import { breakpoint } from "./mixins/breakpoint.js";
import truncate from "./filters/truncate";
import reveal from "./directives/reveal.js";
import zoom from "./directives/zoom.js";
import { hasSlot } from "./mixins/has-slot.js";
import svg4everybody from "svg4everybody/dist/svg4everybody.min.js";
import VTooltip from "v-tooltip";

Vue.use(VTooltip);

import "./services/ga-tracking";

svg4everybody();
import "./sass/main.scss";

// register plugins
Vue.use(ImagePlugin);
Vue.use(ModalPlugin);
Vue.use(UniqueId);
Vue.use(InstantSearch);

// register global mixins
Vue.mixin(breakpoint);
Vue.mixin(hasSlot);

// Import Vuex store
import store from "./store";

Vue.use(Vuex);

/**
 * Directives.
 */
Vue.directive("ahc-reveal", reveal);
Vue.directive("ahc-zoom", zoom);

Vue.filter("truncate", truncate);

/**
 * Components to be loaded globally (always in the bundle)
 */
Vue.component(
  "ahc-navigation",
  require("./components/navigation/navigation.vue").default
);
Vue.component(
  "ahc-masthead",
  require("./components/masthead/masthead.vue").default
);
Vue.component(
  "ahc-predictive-search",
  require("./components/predictive-search/predictive-search.vue").default
);
Vue.component(
  "ahc-hamburger",
  require("./components/hamburger/hamburger.vue").default
);
Vue.component("ahc-icon", require("./components/icon/icon.vue").default);
Vue.component(
  "ahc-skip-nav",
  require("./components/skip-nav/skip-nav.vue").default
);
Vue.component(
  "ahc-block-list",
  require("./components/block-list/block-list.vue").default
);
Vue.component(
  "ahc-post-list",
  require("./components/post-list/post-list.vue").default
);
Vue.component(
  "ahc-slideshow",
  require("./components/slideshow/slideshow.vue").default
);
Vue.component(
  "ahc-exhibition",
  require("./components/exhibition/exhibition.vue").default
);
Vue.component(
  "ahc-exhibition-section",
  require("./components/exhibition-section/exhibition-section.vue").default
);
Vue.component(
  "vue-script-component",
  require("./../node_modules/vue-script-component/src/components/VueScriptComponent.vue")
    .default
);
Vue.component(
  "ahc-federated-search-results",
  require("./components/federated-search-results/federated-search-results.vue")
    .default
);
Vue.component(
  "ahc-federated-search-facet-filter",
  require("./components/federated-search-facet-filter/federated-search-facet-filter.vue")
    .default
);
Vue.component(
  "ahc-federated-search-facet-date-filter",
  require("./components/federated-search-facet-date-filter/federated-search-facet-date-filter.vue")
    .default
);
Vue.component(
  "ahc-federated-search-results",
  require("./components/federated-search-results/federated-search-results.vue")
    .default
);
Vue.component(
  "ahc-paginator",
  require("./components/paginator/paginator.vue").default
);

Vue.component(
  "ahc-federated-search-searchbar",
  require("./components/federated-search-searchbar/federated-search-searchbar.vue")
    .default
);

Vue.component(
  "ahc-federated-search-details",
  require("./components/federated-search-details/federated-search-details.vue")
    .default
);

Vue.component(
  "ahc-federated-search-landing-modal",
  require("./components/federated-search-landing-modal/federated-search-landing-modal.vue")
    .default
);

Vue.component(
  "ahc-federated-search-slideshow",
  require("./components/federated-search-slideshow/federated-search-slideshow.vue")
    .default
);

Vue.component(
  "ahc-federated-search-list-item",
  require("./components/federated-search-list-item/federated-search-list-item.vue")
    .default
);

/**
 * Dynamic Imports
 * For components that should only be loaded on pages which use them,
 * import them dynamically here. The 'webpackChunkName' specifies how the
 * saved chunk file will be named (ie, showcase-lazy.js). Chunked files
 * are saved in the 'dist' folder in the theme root.
 */

const ahcBlockListCard = () =>
  import(
    /* webpackChunkName: "block-list-card-lazy" */ "./components/block-list/block-list-card.vue"
  );

const ahcVideo = () =>
  import(/* webpackChunkName: "video-lazy" */ "./components/video/video.vue");

const ahcAccordion = () =>
  import(
    /* webpackChunkName: "accordion-lazy" */ "./components/accordion/accordion.vue"
  );

const ahcAccordionGroup = () =>
  import(
    /* webpackChunkName: "accordion-group-lazy" */ "./components/accordion/accordion-group.vue"
  );

const ahcExhibitionMasthead = () =>
  import(
    /* webpackChunkName: "exhibition-masthead-lazy" */ "./components/exhibition-masthead/exhibition-masthead.vue"
  );

const ahcExhibitionImage = () =>
  import(
    /* webpackChunkName: "exhibition-image-lazy" */ "./components/exhibition-image/exhibition-image.vue"
  );

const ahcExhibitionImageSequence = () =>
  import(
    /* webpackChunkName: "exhibition-image-sequence-lazy" */ "./components/exhibition-image-sequence/exhibition-image-sequence.vue"
  );

const ahcFederatedSearchResults = () =>
  import(
    /* webpackChunkName: "federated-search-results" */ "./components/federated-search-results/federated-search-results.vue"
  );

const ahcFederatedSearchFacetFilter = () =>
  import(
    /* webpackChunkName: "federated-search-facet-filter" */ "./components/federated-search-facet-filter/federated-search-facet-filter.vue"
  );

const ahcFederatedSearchFacetDateFilter = () =>
  import(
    /* webpackChunkName: "federated-search-facet-date-filter" */ "./components/federated-search-facet-date-filter/federated-search-facet-date-filter.vue"
  );

const ahcFederatedSearchDetails = () =>
  import(
    /* webpackChunkName: "federated-search-details" */ "./components/federated-search-details/federated-search-details.vue"
  );

const ahcFederatedSearchLandingModal = () =>
  import(
    /* webpackChunkName: "federated-search-landing-modal" */ "./components/federated-search-landing-modal/federated-search-landing-modal.vue"
  );

const ahcFederatedSearchSlideshow = () =>
  import(
    /* webpackChunkName: "federated-search-slideshow" */ "./components/federated-search-slideshow/federated-search-slideshow.vue"
  );

const ahcFederatedSearchListItem = () =>
  import(
    /* webpackChunkName: "federated-search-list-item" */ "./components/federated-search-list-item/federated-search-list-item.vue"
  );

const ahcExhibitionIndexList = () =>
  import(
    /* webpackChunkName: "exhibition-index-list-lazy" */ "./components/exhibition-index-list/exhibition-index-list.vue"
  );

const ahcExhibitionIndexListCard = () =>
  import(
    /* webpackChunkName: "exhibition-index-list-card-lazy" */ "./components/exhibition-index-list/exhibition-index-list-card.vue"
  );

const ahcExhibitionSlideshow = () =>
  import(
    /* webpackChunkName: "exhibition-slideshow-lazy" */ "./components/exhibition-slideshow/exhibition-slideshow.vue"
  );

const ahcExhibitionVideo = () =>
  import(
    /* webpackChunkName: "exhibition-video-lazy" */ "./components/exhibition-video/exhibition-video.vue"
  );

const ahcExhibitionImmersiveVideo = () =>
  import(
    /* webpackChunkName: "exhibition-immersive-video-lazy" */ "./components/exhibition-immersive-video/exhibition-immersive-video.vue"
  );

const ahcRoulette = () =>
  import(
    /* webpackChunkName: "roulette-lazy" */ "./components/roulette/roulette.vue"
  );

const ahcSocial = () =>
  import(
    /* webpackChunkName: "social-lazy" */ "./components/social/social.vue"
  );

const ahcResults = () =>
  import(
    /* webpackChunkName: "results-lazy" */ "./components/results/results.vue"
  );

const ahcFilter = () =>
  import(
    /* webpackChunkName: "filter-lazy" */ "./components/filter/filter.vue"
  );

const ahcFederatedLandingSearchbar = () =>
  import(
    /* webpackChunkName: "filter-lazy" */ "./components/federated-search-searchbar/federated-search-searchbar.vue"
  );

const ahcImageTrail = () =>
  import(
    /* webpackChunkName: "image-trail-lazy" */ "./components/copy-shoutout/image-trail/image-trail.vue"
  );

const ahcSearch = () =>
  import(/* webpackChunkName: "search" */ "./components/search/search.vue");

const ahcPaginator = () =>
  import(
    /* webpackChunkName: "paginator" */ "./components/paginator/paginator.vue"
  );

const ahcPlanner = () =>
  import(
    /* webpackChunkName: "planner-lazy" */ "./components/planner/planner.vue"
  );

const ahcPlanBuilder = () =>
  import(
    /* webpackChunkName: "planner-lazy" */ "./components/plan-builder/plan-builder.vue"
  );

const ahcPlannerChart = () =>
  import(
    /* webpackChunkName: "planner-chart-lazy" */ "./components/planner/planner-chart.vue"
  );

const ahcShare = () =>
  import(/* webpackChunkName: "share-lazy" */ "./components/share/share.vue");

const ahcMemberCta = () =>
  import(
    /* webpackChunkName: "member-cta-lazy" */ "./components/member-cta/member-cta.vue"
  );

/**
 * These dynamic imports are also lazy loaded, but are loaded via
 * rel="preload" to load them immediately
 */
const ahcAlert = () =>
  import(
    /* webpackChunkName: "ahc-alert-lazy" */ /* webpackPreload: true */ "./components/alert/alert.vue"
  );

const ahcHero = () =>
  import(
    /* webpackChunkName: "hero-lazy" */ /* webpackPreload: true */ "./components/hero/hero.vue"
  );

/**
 * These dynamic imports are also lazy loaded, but are loaded via
 * rel="prefetch" to defer their loading until after other scripts
 */
// const ahcRevelator = () =>
//   import(
//     /* webpackChunkName: "revelator-lazy" */ /* webpackPrefetch: true */ "./components/revelator/revelator.vue"
//   );

Vue.config.productionTip = false;

/* eslint-disable no-new */
new Vue({
  el: "#app",
  store,
  router,
  comments: true,
  delimiters: ["${", "}"],
  components: {
    ahcAlert,
    ahcBlockListCard,
    ahcFederatedSearchDetails,
    ahcFederatedSearchResults,
    ahcFederatedSearchListItem,
    ahcFederatedSearchLandingModal,
    ahcFederatedLandingSearchbar,
    ahcFederatedSearchSlideshow,
    ahcFederatedSearchFacetFilter,
    ahcFederatedSearchFacetDateFilter,
    ahcHero,
    ahcVideo,
    ahcAccordion,
    ahcAccordionGroup,
    ahcExhibitionMasthead,
    ahcExhibitionImage,
    ahcExhibitionImageSequence,
    ahcExhibitionIndexList,
    ahcExhibitionIndexListCard,
    ahcExhibitionImmersiveVideo,
    ahcExhibitionSlideshow,
    ahcExhibitionVideo,
    ahcFilter,
    ahcRoulette,
    ahcResults,
    ahcSocial,
    ahcImageTrail,
    ahcSearch,
    ahcPaginator,
    ahcPlanner,
    ahcPlanBuilder,
    ahcPlannerChart,
    ahcShare,
    ahcMemberCta,
  },
});
